import { accountIsOldEnoughForKlausModal, klausPromoAllowed } from '@app/constants/klaus';
import { createContext, useContext } from 'react';
import { createUseMessageHook } from '../hooks/createUseMessageHook';

type IMessageContext = Record<MessageNames, IMessage>;

const MessageContext = createContext<IMessageContext>({} as IMessageContext);

interface Props {
  children: React.ReactNode;
}

export interface IMessage {
  isOpen: boolean;
  wasOpen?: boolean;
  open: () => void;
  close: () => void;
  setContext: (context: any) => void;
}

export type MessageNames =
  | 'newservice'
  | 'tutorial'
  | 'multitenant'
  | 'fullcompletion'
  | 'celloreminder'
  | 'klauslaunch'
  | 'klausmerge';

// Show tutorial for new users
const useTutorial = createUseMessageHook({
  name: 'tutorial',
  recurring: false,
  condition({ user, navigation }) {
    return !!(user?.hasPassword && !navigation.dashboardLocked && !navigation.roadBlock.modalOpen);
  },
  order: 10,
});

// A user has entered a new service in the DSE tree
const useNewServiceMessage = createUseMessageHook({
  name: 'newservice',
  recurring: false,
  condition({ user, userValues }) {
    // filter by userValues from the last 24 hours
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    return !!(
      user?.isSubscriber &&
      userValues?.filter((uv) => new Date(uv.createdAt) > oneDayAgo).find((uv) => uv.key.startsWith('NEW_SERVICE'))
    );
  },
  order: 20,
});

// a user has completed all questionnaires
const useFullCompletionMessage = createUseMessageHook({
  name: 'fullcompletion',
  recurring: false,
  condition({ navigation }) {
    return navigation.progress === 1;
  },
  order: 30,
});

const useMultiTenantInfo = createUseMessageHook({
  name: 'multitenant',
  recurring: false,
  condition({ user }) {
    return user?.company.isPrincipal === false;
  },
  order: 30,
});

const useCelloReminder = createUseMessageHook({
  name: 'celloreminder',
  recurring: false,
  condition({ user, navigation }) {
    return (
      (user?.isSubscriber &&
        !user.hasFeature('Whitelabel') &&
        (navigation.progress ?? 0) >= 0.01 &&
        user.company.isPrincipal) ||
      false
    );
  },
  order: 30,
});

// Launch announcement for Klaus, our sibling product for the EU AI Act.
//
// Held back until the user has settled into Dieter: a fresh account arriving at the dashboard for
// the first time gets a full-screen pitch for a different product before it has found its way
// around, which reads as pushy. Two gates for that — the account has to be a few days old, and
// onboarding must not still be running. The banner and the task reach new users in the meantime.
// Cross-promo for our own products has no place in any whitelabel context either — see
// `klausPromoAllowed`.
const useKlausLaunchMessage = createUseMessageHook({
  name: 'klauslaunch',
  recurring: false,
  condition({ user, navigation, whitelabelConfig, whitelabelLoading }) {
    return !!(
      user?.hasPassword &&
      klausPromoAllowed({ whitelabelConfig, whitelabelLoading, hasWhitelabelFeature: user.hasFeature('Whitelabel') }) &&
      accountIsOldEnoughForKlausModal(user.createdAt) &&
      !navigation.onboarding.inProgress &&
      !navigation.dashboardLocked &&
      !navigation.roadBlock.modalOpen
    );
  },
  order: 40,
});

// Merge-Angebot nach dem Login (Klaus ↔ Dieter Sync, Flow F): der Server liefert
// `klausMergeOffer` nur, wenn der User bei Klaus eine unverknüpfte Org hat, seine
// aktive Firma unverknüpft ist und er nicht schon abgelehnt hat. Vor dem Tutorial,
// damit ein Klaus-Nutzer beim ersten Dieter-Login als Erstes diese Frage sieht.
const useKlausMergeOffer = createUseMessageHook({
  name: 'klausmerge',
  recurring: true,
  condition({ user, navigation }) {
    return !!(user?.klausMergeOffer?.orgs?.length && !navigation.dashboardLocked && !navigation.roadBlock.modalOpen);
  },
  order: 5,
});

export function MessageProvider({ children }: Props) {
  const newservice = useNewServiceMessage();
  const tutorial = useTutorial();
  const fullcompletion = useFullCompletionMessage();
  const multitenant = useMultiTenantInfo();
  const celloreminder = useCelloReminder();
  const klauslaunch = useKlausLaunchMessage();
  const klausmerge = useKlausMergeOffer();

  return (
    <MessageContext.Provider
      value={{ newservice, tutorial, fullcompletion, multitenant, celloreminder, klauslaunch, klausmerge }}
    >
      {children}
    </MessageContext.Provider>
  );
}

/**
 * Hook to access the MessageContext.
 *   This hook provides all basic functionalities to show and hide messages based on conditions
 *   of user, tasks or navigation state.
 *   IMPORTANT: A message will only be displayed if the context has been set, by the message component.
 *              This is necessary, to distinguis between messages which share the same name, but have
 *              different context based on props. The context should always be an object, which makes
 *              this message instance unique. It will be automatically transformed to a hash, which is
 *
 * @returns The current context value for MessageContext.
 */
// eslint-disable-next-line react-refresh/only-export-components -- required for context, will not hurt HMR
export function useMessages() {
  return useContext(MessageContext);
}
